import { withRequestDeadline } from "./requestDeadline";
import { reportOnboardingFailure } from "./onboardingTelemetry";
import { User } from "firebase/auth";
import { BrandAsset, BrandKit } from "../models/BrandKit";

// Only a successful completion response can seed this in-memory handoff.
// Keep it scoped to the signed-in user and workspace, never a URL flag.
let completedSetup: { uid: string; workspaceId: string } | null = null;
export function hasJustCompletedOnboarding(
  user: User,
  workspaceId: string
): boolean {
  return (
    completedSetup?.uid === user.uid &&
    completedSetup.workspaceId === workspaceId
  );
}

export type BusinessDraft = Pick<
  BrandKit,
  | "brandName"
  | "businessDescription"
  | "targetAudiences"
  | "website"
  | "tagline"
  | "logoPath"
  | "colors"
  | "fonts"
  | "brandVoiceTone"
  | "brandVoiceInstructions"
  | "primarySocial"
>;
export interface OnboardingState {
  mode?: "business" | "create";
  returnPath?: string;
  step: "business" | "socials" | "engine" | "post" | "complete";
  kitId: string;
  draft: BusinessDraft;
  draftAssets?: BrandAsset[];
  origin: "website" | "manual";
  brandSkipped?: boolean;
  brandSaved?: boolean;
  socialSkipped?: boolean;
  engineReady?: boolean;
  monitoringEnabled?: boolean;
  createdAt?: number;
}
const reportedPollFailures = new Set<string>();
export async function onboardingRequest(
  user: User,
  workspaceId: string,
  path = "",
  body?: unknown,
  method = "POST",
  parentSignal?: AbortSignal
) {
  method = body === undefined && method === "POST" ? "GET" : method;
  let status: number | undefined;
  try {
    return await withRequestDeadline(
      async (signal) => {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE}/api/onboarding${path}`,
          {
            method,
            signal,
            headers: {
              Authorization: `Bearer ${await user.getIdToken()}`,
              "Content-Type": "application/json",
              "x-workspace-id": workspaceId,
            },
            ...(body === undefined ? {} : { body: JSON.stringify(body) }),
          }
        );
        status = response.status;
        const result = await response.json();
        if (!response.ok)
          throw Object.assign(
            new Error(
              result.error || "Could not save setup. Please try again."
            ),
            { status, code: result.code, returnPath: result.returnPath }
          );
        if (path === "/complete" && method === "POST" && body !== undefined) {
          completedSetup = { uid: user.uid, workspaceId };
        }
        if (path === "/ideas-status") reportedPollFailures.delete(workspaceId);
        return result;
      },
      20000,
      path === "/describe"
        ? "Preparing your description took too long."
        : "Saving is taking longer than expected. Please try again; your edits are still here.",
      parentSignal
    );
  } catch (error) {
    if (parentSignal?.aborted) throw error;
    if (path !== "/ideas-status" || !reportedPollFailures.has(workspaceId)) {
      const operation = [
        "",
        "/start",
        "/brand",
        "/describe",
        "/content-check",
        "/activate",
        "/ideas-status",
        "/complete",
      ].includes(path)
        ? path || "/state"
        : "/unknown";
      reportOnboardingFailure(
        `${method} ${operation}`,
        workspaceId,
        status,
        error
      );
      if (path === "/ideas-status") reportedPollFailures.add(workspaceId);
    }
    throw error;
  }
}
export function normalizeWebsite(raw: string): string {
  const value = raw.trim();
  const url = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
  if (
    !["http:", "https:"].includes(url.protocol) ||
    !url.hostname.includes(".") ||
    url.username ||
    url.password ||
    /^[\d.]+$/.test(url.hostname) ||
    /\.(?:local|localhost|internal)\.?$/i.test(url.hostname)
  )
    throw new Error("Enter a website such as yourbusiness.com.");
  return url.href;
}

// First path segments that are Instagram pages, not accounts. The server
// keeps the same list (server/src/services/extraction/instagramProfile.ts).
const INSTAGRAM_PAGES = new Set([
  "p", "reel", "reels", "tv", "stories", "explore", "accounts", "direct",
  "about", "developer", "legal", "web", "s", "ar", "tags", "locations",
  "challenge", "privacy", "terms", "session", "emails", "your_activity",
]);

/** The handle in an instagram.com profile link (not the homepage, a post or a reel), else null. */
export function instagramHandle(raw: string): string | null {
  let url: URL;
  try {
    url = new URL(normalizeWebsite(raw));
  } catch {
    return null;
  }
  const segments = url.pathname.split("/").filter(Boolean);
  if (!/^(www\.|m\.)?instagram\.com$/i.test(url.hostname) || segments.length !== 1) return null;
  const handle = segments[0].toLowerCase();
  return INSTAGRAM_PAGES.has(handle) || !/^[a-z0-9._]{1,30}$/.test(handle) ? null : handle;
}

/** A social network's homepage describes the platform, not the user's business. */
export function getBusinessWebsiteGuidance(raw: string): string | null {
  let url: URL;
  try {
    url = new URL(normalizeWebsite(raw));
  } catch {
    return null;
  }
  if (url.pathname.replace(/\/+$/, "")) return null;
  const host = url.hostname.toLowerCase().replace(/\.$/, "");
  const networks: Record<string, string> = {
    "instagram.com": "Instagram",
    "facebook.com": "Facebook",
    "linkedin.com": "LinkedIn",
    "tiktok.com": "TikTok",
    "pinterest.com": "Pinterest",
    "youtube.com": "YouTube",
    "twitter.com": "X",
    "x.com": "X",
  };
  const match = Object.entries(networks).find(
    ([domain]) => host === domain || host.endsWith(`.${domain}`)
  );
  return match
    ? `This is ${match[1]}’s homepage. Enter your business website, or choose “Set up manually” below.`
    : null;
}
